import { MarketCode } from '@tink/config/src/markets';
import { ConfigurationModel } from '@tink/config/src/tinkLinkConfig';
import { ProductType } from '@tink/core/src/types';
import { ProviderInputValidator } from '../../components/screens/SubmitCredentials/molecules/ProviderInputs/ProviderInputValidator';
import {
  hasHiddenTermsAndScopeMessages,
  hasSkipConsentScreen,
  shouldSkipUserConfirmation,
} from '../../models/configuration';
import { Provider, ProviderEntity } from '../../models/provider';
import { SessionModel } from '../../models/session';

export type ShouldAutoSubmitCredentialsArgs = {
  provider: ProviderEntity;
  configuration: ConfigurationModel;
  userHasReadDisclaimer: boolean;
  isClientVerified: boolean;
  productType: ProductType | undefined;
  personalIdentifier?: SessionModel['personalIdentifier'];
  enableAutoSubmitCredentials?: boolean;
};

export const shouldAutoSubmitCredentials = ({
  provider,
  configuration,
  productType,
  personalIdentifier,
  userHasReadDisclaimer,
  isClientVerified,
  enableAutoSubmitCredentials = true,
}: ShouldAutoSubmitCredentialsArgs): boolean => {
  if (!enableAutoSubmitCredentials) {
    return false;
  }

  const isLegallyCompliant = userHasReadDisclaimer && isClientVerified;
  const providerHasNoFields = !Provider.hasFields(provider);
  const skipUserConfirmation = shouldSkipUserConfirmation(configuration);
  const hiddenTermsAndScopes = hasHiddenTermsAndScopeMessages(configuration);
  const skipConsentScreen = hasSkipConsentScreen(configuration);

  const isAutoSubmitLegal = hiddenTermsAndScopes || isLegallyCompliant;

  if (!isAutoSubmitLegal) {
    return false;
  }

  const skippingSSNScreenEnabled =
    !!personalIdentifier && skipConsentScreen && provider.market === MarketCode.SE;
  const canSkipSSNScreen =
    skippingSSNScreenEnabled &&
    provider.fields.some(
      (field) => new ProviderInputValidator(field).validate(personalIdentifier).onType.isValid
    );

  const isAutoSubmitEligible = providerHasNoFields || canSkipSSNScreen;

  if (productType === ProductType.CREDENTIALS || productType === ProductType.TRANSACTIONS) {
    return isAutoSubmitEligible && skipUserConfirmation;
  }

  return isAutoSubmitEligible;
};
